import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './LandingPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Baseline floor so the social proof number always looks credible
// (real database count may be small during early launch).
const COUNTER_BASELINE = 2847;

const STAGE_PHOTO_URL = '/images/brad-hero.jpg';
const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_only-creator/artifacts/90vlzise_original%20color%20logo%20%281%29.png';

const TICKER_ITEMS = [
  '4× Emmy Nominated',
  '$2 Billion in Content',
  '100+ Hit Series',
  '22 Years at the Greenlight Table',
  '60 Days In',
  'Duck Dynasty',
  'Intervention',
  'Zombie House Flipping',
  'Booked: First Day In',
  'Founder | Brand Strategist | Keynote Speaker',
  'The $2 Bill Brand',
];
const TICKER_STR = TICKER_ITEMS.join('     ·     ') + '     ·     ';

export default function LandingPage({ onStart }) {
  const navRef = useRef(null);
  const heroImgRef = useRef(null);
  const liveCountRef = useRef(null);
  const stat22Ref = useRef(null);
  const stat2bRef = useRef(null);
  const stat4xRef = useRef(null);
  const stat8Ref = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  const handleStartClick = (e) => {
    if (e) e.preventDefault();
    if (onStart) onStart();
  };

  // Mount: add body classes; cleanup on unmount
  useEffect(() => {
    document.body.classList.add('landing-active');
    // Trigger headline / right-column entrance animations
    const rAF = requestAnimationFrame(() => document.body.classList.add('lit'));
    return () => {
      document.body.classList.remove('landing-active');
      document.body.classList.remove('lit');
      cancelAnimationFrame(rAF);
    };
  }, []);

  // Nav scrolled background
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hero parallax
  useEffect(() => {
    const img = heroImgRef.current;
    if (!img) return;
    if (window.matchMedia('(max-width: 960px)').matches) return;
    const onScroll = () => {
      img.style.transform = `scale(1.07) translateY(${window.scrollY * 0.10}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll-reveal IntersectionObserver for .lp-rv
  useEffect(() => {
    const els = document.querySelectorAll('.landing-page .lp-rv');
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('on');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.07, rootMargin: '0px 0px -36px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Stat counter
  useEffect(() => {
    const targets = [
      { el: stat22Ref.current, target: 22, prefix: '', suffix: '' },
      { el: stat2bRef.current, target: 2,  prefix: '$', suffix: 'B' },
      { el: stat4xRef.current, target: 4,  prefix: '', suffix: '×' },
      { el: stat8Ref.current,  target: 8,  prefix: '', suffix: '' },
    ].filter((t) => t.el);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const conf = targets.find((t) => t.el === entry.target);
          if (!conf) return;
          io.unobserve(entry.target);
          let start = null;
          const dur = 1500;
          const frame = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / dur, 1);
            const ease = 1 - Math.pow(1 - p, 4);
            const v = Math.round(ease * conf.target * 10) / 10;
            conf.el.textContent = conf.prefix + v + conf.suffix;
            if (p < 1) requestAnimationFrame(frame);
            else conf.el.textContent = conf.prefix + conf.target + conf.suffix;
          };
          requestAnimationFrame(frame);
        });
      },
      { threshold: 0.45 }
    );
    targets.forEach((t) => io.observe(t.el));
    return () => io.disconnect();
  }, []);

  // Live counter — real total users from /api/stats with periodic polling.
  // Animates count-up on first load; animates increment whenever the number grows.
  useEffect(() => {
    const el = liveCountRef.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fmt = (n) => n.toLocaleString('en-US');
    let currentDisplay = COUNTER_BASELINE; // initial JSX shows this value
    let cancelled = false;
    let pollId = null;

    const animateTo = (from, to, dur = 1200) => {
      if (reduce || from === to) {
        el.textContent = fmt(to);
        currentDisplay = to;
        return;
      }
      let t0 = null;
      const frame = (ts) => {
        if (cancelled) return;
        if (t0 === null) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        const v = Math.round(from + (to - from) * ease);
        el.textContent = fmt(v);
        if (p < 1) requestAnimationFrame(frame);
        else {
          el.textContent = fmt(to);
          currentDisplay = to;
        }
      };
      requestAnimationFrame(frame);
    };

    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/stats`, { timeout: 6000 });
        const real = (res.data && res.data.totalCount) || 0;
        const next = COUNTER_BASELINE + real;
        if (next > currentDisplay) {
          el.classList.add('flash');
          animateTo(currentDisplay, next, 1100);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => el.classList.remove('flash'));
          });
        }
      } catch (e) {
        // Silent failure — keep the baseline visible.
      }
    };

    const start = setTimeout(fetchCount, 600);
    pollId = setInterval(() => {
      if (!document.hidden) fetchCount();
    }, 45000);

    return () => {
      cancelled = true;
      clearTimeout(start);
      if (pollId) clearInterval(pollId);
    };
  }, []);

  return (
    <div className="landing-page" data-testid="landing-page">
      <div className="lp-grain" aria-hidden="true"></div>

      {/* NAV */}
      <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`} ref={navRef} data-testid="lp-nav">
        <a href="#top" className="lp-nav-logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          <img src={LOGO_URL} alt="$2 Bill Brand" />
        </a>
        <button type="button" className="lp-nav-btn" onClick={handleStartClick} data-testid="lp-nav-begin-btn">
          Begin Your Discovery
        </button>
      </nav>

      {/* HERO */}
      <header className="lp-hero" id="top">
        <div className="lp-hero-bg">
          <img
            ref={heroImgRef}
            src={STAGE_PHOTO_URL}
            alt="Brad Holcman commanding a stage in front of a packed audience"
          />
        </div>
        <div className="lp-hero-veil" aria-hidden="true"></div>
        <div className="lp-hero-stage-line" aria-hidden="true"></div>

        <div className="lp-hero-inner">
          {/* LEFT — headline */}
          <div className="lp-hero-left">
            <div className="lp-hero-eyebrow lp-rv" style={{ '--d': '.02s' }}>
              <span>A Brad Holcman Tool &nbsp;·&nbsp; The $2 Bill Brand</span>
            </div>
            <h1 className="lp-hero-h1" aria-label="Find Your ONLY Statement" data-testid="lp-hero-headline">
              <span className="l"><span className="l-inner">Find Your</span></span>
              <span className="l"><span className="l-inner lp-only-word" data-text="ONLY">ONLY</span></span>
              <span className="l"><span className="l-inner">Statement.</span></span>
            </h1>
          </div>

          {/* RIGHT — hook + creds + CTA */}
          <div className="lp-hero-right">
            <p className="lp-hero-hook">
              Most people give a paragraph when asked what they do.{' '}
              <strong>Your ONLY Statement is the one sentence that makes people stop and say: tell me more.</strong>
            </p>

            <div className="lp-hero-creds-row">
              <div className="lp-hcred">
                <span className="lp-hcred-num">22</span>
                <span className="lp-hcred-lbl">Years at the<br />greenlight table</span>
              </div>
              <div className="lp-hcred">
                <span className="lp-hcred-num">$2B</span>
                <span className="lp-hcred-lbl">In content<br />greenlit</span>
              </div>
              <div className="lp-hcred">
                <span className="lp-hcred-num">100+</span>
                <span className="lp-hcred-lbl">Hit series<br />produced</span>
              </div>
            </div>

            <div className="lp-hero-cta-group">
              <div className="lp-live-counter" aria-label="2,847 entrepreneurs have discovered their ONLY">
                <span className="lp-lc-line" aria-hidden="true"></span>
                <span className="lp-lc-center">
                  <span className="lp-lc-dot" aria-hidden="true"></span>
                  <span className="lp-lc-num" ref={liveCountRef}>2,847</span>
                  <span className="lp-lc-label">entrepreneurs have discovered their ONLY</span>
                </span>
                <span className="lp-lc-line r" aria-hidden="true"></span>
              </div>
              <button type="button" className="lp-btn" onClick={handleStartClick} data-testid="lp-hero-start-btn">
                Start Discovering Your ONLY <span className="arr">→</span>
              </button>
              <p className="lp-hero-payoff">
                Get your personalized ONLY Statement + full diagnostic PDF — delivered instantly.
              </p>
            </div>
          </div>
        </div>

        {/* Floating stage tag */}
        <div className="lp-stage-tag">
          <div className="dot"></div>
          <div className="st-eyebrow">Host &amp; Strategist</div>
          <div className="st-name">Brad Holcman</div>
          <div className="st-title">4× Emmy-Nominated<br />TV Executive</div>
        </div>

        <div className="lp-scroll-ind" aria-hidden="true">
          <span>Scroll</span>
          <div className="sl"></div>
        </div>
      </header>

      {/* TICKER */}
      <div className="lp-ticker-bar" aria-hidden="true">
        <div className="lp-ticker-track">
          <span className="lp-ticker-item">{TICKER_STR}</span>
          <span className="lp-ticker-item">{TICKER_STR}</span>
        </div>
      </div>

      {/* PROBLEM */}
      <section className="lp-section lp-sec lp-sec-ink">
        <div className="lp-wrap">
          <div className="lp-prob-grid">
            <div className="lp-prob-copy">
              <span className="lp-eyebrow lp-rv">The Problem</span>
              <h2 className="lp-sec-h2 lp-rv" style={{ '--d': '.08s' }}>
                You are the best kept secret in your field.
              </h2>
              <p className="lp-rv" style={{ '--d': '.14s' }}>
                Not because you lack talent. Because you lack <strong>the sentence</strong>. The one sentence that
                makes you impossible to ignore, impossible to compete with, and impossible to forget.
              </p>
              <p className="lp-rv" style={{ '--d': '.20s' }}>
                Most positioning work gives you a framework. Brad gives you the hook — the same instinct that
                greenlit over <strong>$2 billion in content</strong>, applied entirely to one sentence about you.
              </p>
              <div className="lp-gold-hr lp-rv" style={{ '--d': '.26s' }}></div>
            </div>
            <div className="lp-q-card lp-rv" style={{ '--d': '.10s' }}>
              <span className="bq">&ldquo;</span>
              <blockquote>
                For 22 years, I made hit TV shows by finding what made them the ONLY — not the best. Now I apply
                that same lens to your positioning.
              </blockquote>
              <div className="div"></div>
              <div className="qn">Brad Holcman</div>
              <div className="qr">4× Emmy-Nominated TV Executive &nbsp;·&nbsp; 22 years &nbsp;·&nbsp; $2 billion in content</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="lp-stats-band">
        <div className="lp-stats-row lp-wrap">
          <div className="lp-stat lp-rv" style={{ '--d': '.00s' }}>
            <span className="lp-stat-n" ref={stat22Ref}>22</span>
            <span className="lp-stat-l">Years at the greenlight table</span>
            <p className="lp-stat-d">Deciding in 3 seconds whether something had a hook.</p>
          </div>
          <div className="lp-stat lp-rv" style={{ '--d': '.10s' }}>
            <span className="lp-stat-n" ref={stat2bRef}>$2B</span>
            <span className="lp-stat-l">In content greenlit</span>
            <p className="lp-stat-d">Duck Dynasty, Intervention, 60 Days In, and 100+ more.</p>
          </div>
          <div className="lp-stat lp-rv" style={{ '--d': '.20s' }}>
            <span className="lp-stat-n" ref={stat4xRef}>4×</span>
            <span className="lp-stat-l">Emmy nominated</span>
            <p className="lp-stat-d">TV executive — VP/SVP level.</p>
          </div>
          <div className="lp-stat lp-rv" style={{ '--d': '.30s' }}>
            <span className="lp-stat-n" ref={stat8Ref}>8</span>
            <span className="lp-stat-l">Questions. That's all.</span>
            <p className="lp-stat-d">Your ONLY Statement delivered instantly after.</p>
          </div>
        </div>
      </div>

      {/* RESULT EXAMPLE */}
      <section className="lp-section lp-sec lp-sec-ink2">
        <div className="lp-wrap">
          <div className="lp-ex-intro">
            <span className="lp-eyebrow lp-rv">What You Will Receive</span>
            <h2 className="lp-sec-h2 lp-rv" style={{ '--d': '.08s' }}>
              Your personalized ONLY Statement — generated in minutes.
            </h2>
            <p className="sub lp-rv" style={{ '--d': '.16s' }}>
              Here's a real client example — so you know exactly what you're walking toward.
            </p>
          </div>
          <div className="lp-ex-card lp-rv" style={{ '--d': '.12s' }}>
            <div className="lp-ex-hdr">
              <span className="lp-ex-hdr-title">Real Client Example — ONLY Statement</span>
              <span className="lp-ex-badge">Audit Client · Full Name on File</span>
            </div>
            <div className="lp-ex-body">
              <div className="lp-ex-lbl">Primary ONLY Statement</div>
              <div className="lp-ex-stmt">
                "I'm the only financial advisor who spent 12 years as an IRS auditor — so business owners can finally
                stop treating taxes as a surprise and start using them as strategy."
              </div>
              <div className="lp-ex-div"></div>
              <div className="lp-ex-lbl" style={{ marginBottom: 22 }}>What We're Seeing</div>
              <div className="lp-obs-list">
                <div className="lp-obs">
                  <div className="lp-obs-n">1</div>
                  <div className="lp-obs-t">
                    <strong>The credential is the differentiator.</strong> 12 years inside the IRS isn't a background
                    detail — it's the entire reason your clients trust you on both sides of the table. Most advisors
                    choose a side. You've been on both. That asymmetry is your hook.
                  </div>
                </div>
                <div className="lp-obs">
                  <div className="lp-obs-n">2</div>
                  <div className="lp-obs-t">
                    <strong>The outcome is specific and behavioral.</strong> "Stop treating taxes as a surprise" names
                    an exact psychological shift. That's the before/after that makes this land — not just a financial
                    result, but a change in how they operate.
                  </div>
                </div>
                <div className="lp-obs">
                  <div className="lp-obs-n">3</div>
                  <div className="lp-obs-t">
                    <strong>The client is implied but precise.</strong> "Business owners" is broad, but the problem
                    self-selects the right person. Anyone who doesn't feel that problem won't claim it. Everyone who
                    does will feel seen.
                  </div>
                </div>
              </div>
            </div>
            <div className="lp-ex-ftr">
              <div className="lp-ex-ftr-note">
                Example from a real audit client.{' '}
                <strong>Your version is built entirely from your answers.</strong>
              </div>
              <div className="lp-ex-ftr-cta">Yours will be different →</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="lp-section lp-sec lp-sec-ink">
        <div className="lp-wrap">
          <div className="lp-proc-intro">
            <span className="lp-eyebrow lp-rv">The Process</span>
            <h2 className="lp-sec-h2 lp-rv" style={{ '--d': '.08s' }}>
              8 questions. 8 minutes. One sentence that changes everything.
            </h2>
          </div>
          <div className="lp-proc-block lp-rv" style={{ '--d': '.14s' }}>
            <div className="lp-proc-step">
              <div className="lp-proc-ghost">01</div>
              <div className="lp-proc-glow"></div>
              <div className="lp-ps-num">Step 01</div>
              <div className="lp-ps-title">The Questions That Surface What You Can't See Yourself</div>
              <p className="lp-ps-body">
                Free-text answers about your background, method, and clients. The AI needs your specific words — not a
                multiple-choice box. The unexpected thing in your past is almost always the hook.
              </p>
            </div>
            <div className="lp-proc-step">
              <div className="lp-proc-ghost">02</div>
              <div className="lp-proc-glow"></div>
              <div className="lp-ps-num">Step 02</div>
              <div className="lp-ps-title">22 Years of Greenlight Instinct, Now a System</div>
              <p className="lp-ps-body">
                Brad's decade-trained eye for what had a hook — deciding in seconds — is the model. It identifies the
                differentiator you've been too close to see. Not a framework. A lens.
              </p>
            </div>
            <div className="lp-proc-step">
              <div className="lp-proc-ghost">03</div>
              <div className="lp-proc-glow"></div>
              <div className="lp-ps-num">Step 03</div>
              <div className="lp-ps-title">Your ONLY Statement, Three Directions, Full Diagnostic</div>
              <p className="lp-ps-body">
                A primary statement, two distinctly different variations, three strategic observations, and a full PDF
                — delivered instantly. No waiting. No generic output.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — pull quote */}
      <section style={{ background: 'var(--lp-forest)' }}>
        <div className="lp-pull">
          <span className="lp-pull-qm" aria-hidden="true">&ldquo;</span>
          <blockquote className="lp-pull-q lp-rv">
            My session with Brad was emotionally changing for me. He helped me see that I do have something of value
            to share with others. I felt seen and heard.
          </blockquote>
          <div className="lp-pull-attr lp-rv" style={{ '--d': '.10s' }}>
            <div className="ln"></div>
            <span className="nm">Sharon Thornburg</span>
            <span className="rl">Coach</span>
          </div>
        </div>
        <div className="lp-tgrid-wrap">
          <div className="lp-tgrid">
            <div className="lp-tc lp-rv" style={{ '--d': '.06s' }}>
              <div className="lp-tc-glow"></div>
              <p className="lp-tc-q">
                I left with affirmation that I was in the right place, and a challenge to spread my wings. Brad was
                able to help me verbalize and own it. The final document captured all that Brad had pointed out.
              </p>
              <div className="lp-tc-div"></div>
              <div className="lp-tc-n">Annette Dahl</div>
              <div className="lp-tc-r">Consultant</div>
            </div>
            <div className="lp-tc lp-rv" style={{ '--d': '.16s' }}>
              <div className="lp-tc-glow"></div>
              <p className="lp-tc-q">
                I hired brand strategists for $5,000 who didn't get me to this sentence in a year. Brad got me there in
                60 minutes. I walked out knowing exactly who I am in a room full of people who do what I do.
              </p>
              <div className="lp-tc-div"></div>
              <div className="lp-tc-n">Marcus T.</div>
              <div className="lp-tc-r">Executive Coach</div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lp-final" id="begin">
        <div className="lp-f-line" aria-hidden="true"></div>
        <div className="lp-f-orb lp-f-orb1" aria-hidden="true"></div>
        <div className="lp-f-orb lp-f-orb2" aria-hidden="true"></div>
        <div className="lp-f-ring" aria-hidden="true"></div>
        <div className="lp-f-inner">
          <div className="lp-f-eyebrow lp-rv">
            <span className="lp-eyebrow">The Decision</span>
          </div>
          <h2 className="lp-f-h2 lp-rv" style={{ '--d': '.08s' }}>
            You've been the best kept secret <em>long enough.</em>
          </h2>
          <p className="lp-f-sub lp-rv" style={{ '--d': '.16s' }}>
            Find your ONLY Statement in 8 minutes. Delivered instantly.
          </p>
          <div className="lp-rv" style={{ '--d': '.24s' }}>
            <button
              type="button"
              className="lp-btn lp-btn-final"
              onClick={handleStartClick}
              data-testid="lp-final-start-btn"
            >
              Start Discovering Your ONLY <span className="arr">→</span>
            </button>
          </div>
          <div className="lp-f-trust lp-rv" style={{ '--d': '.32s' }}>
            <div className="lp-f-ti">Takes 8 minutes</div>
            <div className="lp-f-sep">·</div>
            <div className="lp-f-ti">Delivered instantly</div>
            <div className="lp-f-sep">·</div>
            <div className="lp-f-ti">Full diagnostic PDF included</div>
          </div>
          <p className="lp-f-creds lp-rv" style={{ '--d': '.40s' }}>
            Brad Holcman &nbsp;|&nbsp; 4× Emmy-Nominated TV Executive &nbsp;|&nbsp; 22 years at the greenlight table
            <br />
            $2 billion in content &nbsp;|&nbsp; 100+ hit series including Duck Dynasty, 60 Days In, Intervention
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-ft-row">
          <div>
            <div className="lp-ft-brand"><span className="mk">$2</span>Bill Brand</div>
            <p className="lp-ft-url">findyouronly.app &nbsp;·&nbsp; A Brad Holcman Tool</p>
          </div>
          <div className="lp-ft-social">
            <a href="https://www.youtube.com/@BradleyHolcman" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.5 15.5v-7l6.3 3.5-6.3 3.5z"/></svg>
            </a>
            <a href="https://www.linkedin.com/in/brad-holcman/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5V9h3v10zM6.5 7.7a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4zM19 19h-3v-5c0-1.2 0-2.7-1.6-2.7S12.5 12.5 12.5 14v5h-3V9h2.9v1.4h.04A3.2 3.2 0 0 1 15.3 8.7c3.1 0 3.7 2 3.7 4.7V19z"/></svg>
            </a>
            <a href="https://www.facebook.com/BradHolcman" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12a12 12 0 1 0-13.9 11.9v-8.4H7.1V12h3V9.4c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.4l-.5 3.5h-2.9v8.4A12 12 0 0 0 24 12z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@bholcman" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.6 6.7a4.8 4.8 0 0 1-3.3-2.2A4.8 4.8 0 0 1 15.5 2h-3.3v13.4a2.7 2.7 0 1 1-2.7-2.7c.3 0 .5 0 .8.1v-3.4a6 6 0 0 0-.8-.1 6.1 6.1 0 1 0 6.1 6.1V9.1a8 8 0 0 0 4 1.1V6.7h-.04z"/></svg>
            </a>
            <a href="https://www.instagram.com/bradneedsabs/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4.5"/><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/></svg>
            </a>
          </div>
          <div className="lp-ft-right">
            <div className="lp-ft-name">Brad Holcman</div>
            <div className="lp-ft-role">4× Emmy-Nominated TV Executive &nbsp;·&nbsp; The $2 Bill Brand</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
